import React, { useCallback, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, Checkbox, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import type { DriftMoneyMode, DriftRow, DriftSubRow, DriftViewState } from "store/types";

import { AumInlineEditor } from "../AumInlineEditor";
import {
  COLOR_BAR_ACTUAL,
  COLOR_BAR_TARGET,
  COLOR_BG_SUBTLE,
  COLOR_DISABLED,
  COLOR_NEUTRAL,
  COLOR_NEUTRAL_BG,
  COLOR_OVER,
  COLOR_OVER_BG,
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  COLOR_STROKE_DIVIDER,
  COLOR_UNDER,
  COLOR_UNDER_BG
} from "../colors";
import { usePortfolioConstructionContext } from "../Context/PortfolioConstructionContext";

type MoneyMode = DriftMoneyMode;

// Custom and system asset classes have independent id spaces, so a row's identity
// is the (type + id) pair — using id alone collides (duplicate rows, broken expand).
const rowKey = (row: DriftRow) => `${row.asset_class_type}:${row.asset_class_id}`;

// Format a drift figure as USD currency in "$" mode, or as a percentage in "%" mode.
const formatValue = (value: number, mode: MoneyMode): string => {
  const n = Number.isFinite(value) ? value : 0;
  return mode === "pct"
    ? new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n / 100)
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n);
};

export const AllocationDrift: React.FC = () => {
  const {
    activeSliceKey,
    targetPolicy,
    permissions,
    onOpenTargetPolicy,
    exposureMode,
    setExposureMode,
    aum,
    updateAum,
    aumError,
    clearAumError,
    onAddAssetClass,
    driftRows
  } = usePortfolioConstructionContext();

  // Drift view (money/pct mode, empty toggle, expanded rows) is local — only this
  // component reads it, so keeping it out of the context avoids re-rendering the
  // rest of the panel on every toggle.
  const [driftView, setDriftView] = useState<DriftViewState>({ moneyMode: "pct", showEmpty: false, expanded: [] });

  const moneyMode = driftView.moneyMode;
  const showEmpty = driftView.showEmpty;
  const setMoneyMode = useCallback((mode: MoneyMode) => setDriftView((prev) => ({ ...prev, moneyMode: mode })), []);
  const setShowEmpty = useCallback((next: boolean) => setDriftView((prev) => ({ ...prev, showEmpty: next })), []);

  const expandedSet = useMemo(() => new Set(driftView.expanded), [driftView.expanded]);
  const toggleExpanded = useCallback(
    (id: string) =>
      setDriftView((prev) => {
        const next = new Set(prev.expanded);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { ...prev, expanded: Array.from(next) };
      }),
    []
  );

  const aumValue = aum ?? 0;

  // Fixed for the session — compute once instead of building a Date on every render.
  const asOfDate = useMemo(
    () => new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    []
  );

  const visibleRows = useMemo(() => {
    if (showEmpty) return driftRows;
    return driftRows.filter((r) => (r.actual_investment ?? 0) > 0 || (r.target ?? 0) > 0);
  }, [driftRows, showEmpty]);

  const totals = useMemo(() => {
    const isPct = moneyMode === "pct";
    const actual = visibleRows.reduce((s, r) => s + ((isPct ? r.actual_percentage : r.actual_investment) ?? 0), 0);
    const target = visibleRows.reduce((s, r) => s + ((isPct ? r.target_percent : r.target) ?? 0), 0);
    return { actual, target, drift: actual - target };
  }, [visibleRows, moneyMode]);

  // Largest allocation in view sets the full-width reference for the visual bars.
  const barScale = useMemo(() => {
    const max = visibleRows.reduce((m, r) => Math.max(m, r.actual_percentage ?? 0, r.target_percent ?? 0), 0);
    return max > 0 ? max : 100;
  }, [visibleRows]);

  return (
    <Stack
      sx={{
        width: "100%",
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "16px",
        gap: "12px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap="16px" sx={{ width: "100%" }}>
        <Stack sx={{ minWidth: 0, gap: "6px" }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "24px",
              color: COLOR_PRIMARY
            }}
          >
            Allocation Drift
          </Typography>
          <AumDisplay
            aum={aumValue}
            onSave={updateAum}
            onEditStart={clearAumError}
            disabled={!activeSliceKey || !permissions.canEditTargetPolicy}
          />
          {aumError && (
            <Typography sx={{ color: COLOR_OVER, fontSize: "12px", lineHeight: "16px" }}>{aumError}</Typography>
          )}
        </Stack>
        <Stack alignItems="flex-end" gap="8px">
          <Typography
            sx={{
              fontSize: "13px",
              lineHeight: "18px",
              color: COLOR_SECONDARY
            }}
          >
            As of {asOfDate}
          </Typography>
          <Stack direction="row" alignItems="center" gap="10px">
            <Stack direction="row" alignItems="center" gap="6px">
              <MaterialTooltip title="Adjusted NAV only" isShow placement="top">
                <Box>
                  <PillChip label="NAV" active={exposureMode === "nav"} onClick={() => setExposureMode("nav")} />
                </Box>
              </MaterialTooltip>
              <MaterialTooltip title="NAV + all unfunded commitment (existing + planned)">
                <Box>
                  <PillChip
                    label="Exposure"
                    active={exposureMode === "exposure"}
                    onClick={() => setExposureMode("exposure")}
                  />
                </Box>
              </MaterialTooltip>
            </Stack>
            <Stack direction="row" alignItems="center" gap="4px">
              <PillChip label="%" active={moneyMode === "pct"} onClick={() => setMoneyMode("pct")} compact />
              <PillChip label="$" active={moneyMode === "money"} onClick={() => setMoneyMode("money")} compact />
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px" sx={{ width: "100%" }}>
        <Stack direction="row" alignItems="center" gap="8px">
          <Checkbox
            checked={showEmpty}
            onClick={(e) => e.stopPropagation()}
            onChange={() => setShowEmpty(!showEmpty)}
            size="small"
            name="drift-show-empty"
          />
          <Typography sx={{ fontSize: "12px", fontWeight: 400, lineHeight: "16px" }}>
            Show Empty Asset Classes
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap="8px">
          {permissions.canEditTargetPolicy && targetPolicy && (
            <Button
              onClick={onOpenTargetPolicy}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: "#26a6ae",
                backgroundColor: "transparent",
                padding: "4px 8px",
                minWidth: 0,
                minHeight: 0,

                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                textTransform: "none",
                borderRadius: "32px",
                "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
                "&:hover": { backgroundColor: "rgba(38, 166, 174, 0.08)" }
              }}
            >
              Edit policy
            </Button>
          )}
          {permissions.canEditTargetPolicy && (
            <Button
              onClick={onAddAssetClass}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: "#26a6ae",
                backgroundColor: "transparent",
                padding: "4px 8px",
                minWidth: 0,
                minHeight: 0,

                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                textTransform: "none",
                borderRadius: "32px",
                "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
                "&:hover": { backgroundColor: "rgba(38, 166, 174, 0.08)" }
              }}
            >
              Add Asset Class
            </Button>
          )}
        </Stack>
      </Stack>

      {exposureMode === "exposure" && (
        <Stack
          sx={{
            backgroundColor: "#fff7e6",
            border: "1px solid #f5b14b",
            borderRadius: "6px",
            padding: "8px 12px"
          }}
        >
          <Typography sx={{ color: "#7a4400", fontSize: "13px", lineHeight: "18px" }}>
            Showing NAV + unfunded commitments from planned deals. Actual figures are forward-looking.
          </Typography>
        </Stack>
      )}

      <Stack sx={{ border: `1px solid ${COLOR_STROKE_DIVIDER}`, borderRadius: "6px", overflow: "hidden" }}>
        <TableHeader />
        {visibleRows.length === 0 ? (
          <Stack sx={{ padding: "20px", alignItems: "center", borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}` }}>
            <Typography sx={{ color: COLOR_DISABLED, fontSize: "13px" }}>No asset classes to display.</Typography>
          </Stack>
        ) : (
          visibleRows.map((row) => {
            const key = rowKey(row);
            return (
              <DataRow
                key={key}
                row={row}
                moneyMode={moneyMode}
                barScale={barScale}
                showEmpty={showEmpty}
                expanded={expandedSet.has(key)}
                onToggle={toggleExpanded}
              />
            );
          })
        )}
        {Boolean(visibleRows.length) && (
          <TotalRowView actual={totals.actual} target={totals.target} drift={totals.drift} moneyMode={moneyMode} />
        )}
      </Stack>
    </Stack>
  );
};

const LegendDot: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
);

const TableHeader: React.FC = () => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      padding: "10px 12px",
      backgroundColor: COLOR_BG_SUBTLE,
      borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`
    }}
  >
    <Typography sx={cellLabelSx({ flex: 1 })}>Allocation Hierarchy</Typography>
    <Stack direction="row" alignItems="center" gap="6px" sx={{ width: 150, flexShrink: 0, justifyContent: "flex-end" }}>
      <LegendDot color={COLOR_BAR_ACTUAL} />
      <Typography sx={cellLabelSx({})}>Actual Investment</Typography>
    </Stack>
    <Stack direction="row" alignItems="center" gap="6px" sx={{ width: 150, flexShrink: 0, justifyContent: "flex-end" }}>
      <LegendDot color={COLOR_BAR_TARGET} />
      <Typography sx={cellLabelSx({})}>Target Investment</Typography>
    </Stack>
    <Typography sx={cellLabelSx({ width: 110, textAlign: "right" })}>Delta</Typography>
    <Typography sx={cellLabelSx({ width: 200, ml: "12px" })}>Visual</Typography>
  </Stack>
);

const cellLabelSx = (extra: Record<string, unknown>) => ({
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "16px",
  color: COLOR_SECONDARY,
  flexShrink: 0,
  ...extra
});

interface DataRowProps {
  row: DriftRow;
  moneyMode: MoneyMode;
  barScale: number;
  showEmpty: boolean;
  expanded: boolean;
  onToggle: (key: string) => void;
}

const DataRow = React.memo(function DataRow({
  row,
  moneyMode,
  barScale,
  showEmpty,
  expanded,
  onToggle
}: DataRowProps) {
  const isPct = moneyMode === "pct";
  const actual = (isPct ? row.actual_percentage : row.actual_investment) ?? 0;
  const target = (isPct ? row.target_percent : row.target) ?? 0;
  const drift = (isPct ? row.delta_percentage : row.delta) ?? 0;
  const hasTarget = (row.target_percent ?? 0) > 0 || (row.target ?? 0) > 0;
  // bar always uses percentage fields (independent of money/pct toggle)
  const actualPct = row.actual_percentage ?? 0;
  const targetPct = row.target_percent ?? 0;
  const tone = driftTone(drift, hasTarget ? 1 : 0);

  const subRows = showEmpty
    ? row.sub_rows
    : row.sub_rows.filter((s) => (s.actual_investment ?? 0) > 0 || (s.target ?? 0) > 0);
  const canExpand = subRows.length > 0;

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          padding: "10px 12px",
          borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
          backgroundColor: "white"
        }}
      >
        <Stack direction="row" alignItems="center" gap="4px" sx={{ flex: 1, minWidth: 0 }}>
          {canExpand ? (
            <Box
              component="button"
              onClick={() => onToggle(rowKey(row))}
              aria-label={expanded ? "Collapse" : "Expand"}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                flexShrink: 0,
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
                color: COLOR_SECONDARY,
                "&:hover": { color: COLOR_PRIMARY }
              }}
            >
              {expanded ? (
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
              )}
            </Box>
          ) : (
            <Box sx={{ width: 20, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              color: COLOR_PRIMARY,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {row.asset_class_name}
          </Typography>
        </Stack>
        <Typography sx={valueSx({ width: 150 })}>{formatValue(actual, moneyMode)}</Typography>
        <Typography sx={valueSx({ width: 150 })}>{hasTarget ? formatValue(target, moneyMode) : "—"}</Typography>
        <Box sx={{ width: 110, textAlign: "right", flexShrink: 0 }}>
          <DeltaBadge value={drift} moneyMode={moneyMode} tone={hasTarget ? tone : "neutral"} />
        </Box>
        <Box sx={{ width: 200, flexShrink: 0, ml: "12px" }}>
          {actualPct > 0 || targetPct > 0 ? (
            <DriftBar actualPct={actualPct} targetPct={targetPct} maxPct={barScale} />
          ) : (
            <Box sx={{ height: 8 }} />
          )}
        </Box>
      </Stack>

      {expanded &&
        subRows.map((sub) => (
          <SubRowView key={String(sub.sub_asset_class_id)} sub={sub} moneyMode={moneyMode} barScale={barScale} />
        ))}
    </>
  );
});

interface SubRowProps {
  sub: DriftSubRow;
  moneyMode: MoneyMode;
  barScale: number;
}

const SubRowView: React.FC<SubRowProps> = ({ sub, moneyMode, barScale }) => {
  const isPct = moneyMode === "pct";
  const actual = (isPct ? sub.actual_percentage : sub.actual_investment) ?? 0;
  const target = (isPct ? sub.target_percent : sub.target) ?? 0;
  const drift = (isPct ? sub.delta_percentage : sub.delta) ?? 0;
  const hasTarget = (sub.target_percent ?? 0) > 0 || (sub.target ?? 0) > 0;
  const actualPct = sub.actual_percentage ?? 0;
  const targetPct = sub.target_percent ?? 0;
  const tone = driftTone(drift, hasTarget ? 1 : 0);
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        padding: "8px 12px",
        borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
        backgroundColor: COLOR_BG_SUBTLE
      }}
    >
      <Stack direction="row" alignItems="center" gap="4px" sx={{ flex: 1, minWidth: 0, pl: "32px" }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            lineHeight: "18px",
            color: COLOR_SECONDARY,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {sub.sub_asset_class_name}
        </Typography>
      </Stack>
      <Typography sx={valueSx({ width: 150, color: COLOR_SECONDARY })}>{formatValue(actual, moneyMode)}</Typography>
      <Typography sx={valueSx({ width: 150, color: COLOR_SECONDARY })}>
        {hasTarget ? formatValue(target, moneyMode) : "—"}
      </Typography>
      <Box sx={{ width: 110, textAlign: "right", flexShrink: 0 }}>
        <DeltaBadge value={drift} moneyMode={moneyMode} tone={hasTarget ? tone : "neutral"} />
      </Box>
      <Box sx={{ width: 200, flexShrink: 0, ml: "12px" }}>
        {actualPct > 0 || targetPct > 0 ? (
          <DriftBar actualPct={actualPct} targetPct={targetPct} maxPct={barScale} />
        ) : (
          <Box sx={{ height: 8 }} />
        )}
      </Box>
    </Stack>
  );
};

const valueSx = (extra: Record<string, unknown>) => ({
  fontSize: "14px",
  lineHeight: "20px",
  color: COLOR_PRIMARY,
  textAlign: "right",
  flexShrink: 0,
  ...extra
});

interface TotalRowViewProps {
  actual: number;
  target: number;
  drift: number;
  moneyMode: MoneyMode;
}

const TotalRowView: React.FC<TotalRowViewProps> = ({ actual, target, drift, moneyMode }) => {
  const tone = driftTone(drift, target);
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        padding: "10px 12px",
        backgroundColor: COLOR_BG_SUBTLE,
        borderTop: `2px solid ${COLOR_STROKE_DIVIDER}`
      }}
    >
      <Stack direction="row" alignItems="center" gap="4px" sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            color: COLOR_PRIMARY
          }}
        >
          Total
        </Typography>
      </Stack>
      <Typography sx={valueSx({ width: 150, fontWeight: 600 })}>{formatValue(actual, moneyMode)}</Typography>
      <Typography sx={valueSx({ width: 150, fontWeight: 600 })}>
        {target > 0 ? formatValue(target, moneyMode) : "—"}
      </Typography>
      <Box sx={{ width: 110, textAlign: "right", flexShrink: 0 }}>
        <DeltaBadge value={drift} moneyMode={moneyMode} tone={target > 0 ? tone : "neutral"} />
      </Box>
      <Box sx={{ width: 200, flexShrink: 0, ml: "12px" }} />
    </Stack>
  );
};

type DeltaTone = "over" | "under" | "neutral";

const driftTone = (drift: number, target: number): DeltaTone => {
  if (!(target > 0)) return "neutral";
  if (drift > 0) return "over";
  if (drift < 0) return "under";
  return "neutral";
};

interface DeltaBadgeProps {
  value: number;
  tone: DeltaTone;
  moneyMode: MoneyMode;
}

const DeltaBadge: React.FC<DeltaBadgeProps> = ({ value, tone, moneyMode }) => {
  const palette =
    tone === "over"
      ? { bg: COLOR_OVER_BG, fg: COLOR_OVER }
      : tone === "under"
        ? { bg: COLOR_UNDER_BG, fg: COLOR_UNDER }
        : { bg: COLOR_NEUTRAL_BG, fg: COLOR_NEUTRAL };
  return (
    <Box
      sx={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "10px",
        backgroundColor: palette.bg,
        color: palette.fg,

        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "16px"
      }}
    >
      {formatValue(value, moneyMode)}
    </Box>
  );
};

interface DriftBarProps {
  actualPct: number;
  targetPct: number;
  maxPct: number;
}

const DriftBar: React.FC<DriftBarProps> = ({ actualPct, targetPct, maxPct }) => {
  const actualWidth = Math.min(100, Math.max(0, (actualPct / maxPct) * 100));
  const targetWidth = Math.min(100, Math.max(0, (targetPct / maxPct) * 100));
  return (
    <Stack sx={{ width: "100%", gap: "6px" }}>
      <Box
        sx={{
          height: 8,
          width: `${actualWidth}%`,
          backgroundColor: COLOR_BAR_ACTUAL,
          borderRadius: "4px",
          transition: "width 200ms ease"
        }}
      />
      <Box
        sx={{
          height: 8,
          width: `${targetWidth}%`,
          backgroundColor: COLOR_BAR_TARGET,
          borderRadius: "4px",
          transition: "width 200ms ease"
        }}
      />
    </Stack>
  );
};

interface PillChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  compact?: boolean;
}

const PillChip: React.FC<PillChipProps> = ({ label, active, onClick, compact }) => (
  <Box
    onClick={onClick}
    sx={{
      height: "24px",
      padding: compact ? "0" : "0 10px",
      width: compact ? "34px" : undefined,
      borderRadius: "20px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: active ? "#071f35" : "#ecf1f8",
      color: active ? "white" : "#071f35",

      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "16px",
      whiteSpace: "nowrap"
    }}
  >
    {label}
  </Box>
);

interface AumDisplayProps {
  aum: number;
  onSave: (next: number) => void;
  onEditStart?: () => void;
  disabled: boolean;
}

const AumDisplay: React.FC<AumDisplayProps> = ({ aum, onSave, onEditStart, disabled }) => (
  <Stack direction="row" alignItems="center" gap="8px" sx={{ height: "30px" }}>
    <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: COLOR_SECONDARY }}>AUM</Typography>
    <AumInlineEditor value={aum} canEdit={!disabled} onSave={onSave} onEditStart={onEditStart} />
  </Stack>
);
